import { Form, Modal, message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { gql, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { withApollo } from 'react-apollo';
import { fetchFormsByConnectionId } from 'src/_shared/services/utils.js';

import NominateEmployeePresentation from '../../presentation/NominateEmployeePresentation';

import { nominationSchema } from './validationSchema.js';

const CREATE_NOMINATION = gql`
	mutation CreateNomination($input: NominationInput!) {
		createNomination(input: $input) {
			updatedAt
			reason
			nomineeId
			nominee {
				id
				firstName
				lastName
			}
			nominatorId
			nominator {
				id
				firstName
				lastName
			}
			nominationReason
			nominationId
			nominationConfigurationId
			nominationConfiguration {
				id
				createdAt
				startDate
				status
				updatedAt
				badgeId
				companyId
				configId
				customFormId
				endDate
				awardedCount
				nominationConfigurationId
				nominatorEmployeeLevelId
				nominatorEmployeePayTypeId
				nominatorEmployeelogInTypeId
				nomineeEmployeeLevelId
				nomineeEmployeePayTypeId
				nomineeEmployeelogInTypeId
				snappyCampaignTierId
				snappyGrantId
			}
			itemType
			isWinner
			id
			createdAt
			configId
			companyId
			customFormAnswers
		}
	}
`;

const NominateEmployeeModalContainer = ({
	showModal,
	closeModal,
	nominationConfigId,
	customFormId,
	badgeDetails,
	endDate,
	client,
}) => {
	const currentUser = useSelector((state) => state?.user?.currentUser);
	const [selectedEmployee, setSelectedEmployee] = useState(null);
	const [searchedEmployeeData, setSearchedEmployeeData] = useState([]);
	const [customFormDefinition, setCustomFormDefinition] = useState(null);
	const [customFormLoading, setCustomFormLoading] = useState(false);
	const [customFormInstance] = Form.useForm();

	const [createNomination, { loading: nominationLoading }] =
		useMutation(CREATE_NOMINATION);

	useEffect(() => {
		if (!showModal || !customFormId || !currentUser?.companyId) {
			setCustomFormDefinition(null);
			return;
		}

		const loadCustomForm = async () => {
			setCustomFormLoading(true);
			try {
				const forms = await fetchFormsByConnectionId(
					currentUser,
					'nomination_form',
					client
				);
				const matched = forms.find(
					(form) => form?.metadata?.id === customFormId
				);
				setCustomFormDefinition(matched || null);
			} catch (error) {
				console.error('Error loading custom nomination form:', error);
				setCustomFormDefinition(null);
			} finally {
				setCustomFormLoading(false);
			}
		};

		loadCustomForm();
	}, [showModal, customFormId, currentUser?.companyId]);

	const formik = useFormik({
		initialValues: {
			nominationConfigurationId: nominationConfigId,
			nomineeId: selectedEmployee || '',
			nominationReason: '',
		},
		validationSchema: nominationSchema,
		onSubmit: () => {
			if (customFormDefinition) {
				customFormInstance.validateFields().then((customFormValues) => {
					confirm(customFormValues);
				}).catch(() => {});
			} else {
				confirm(null);
			}
		},
	});

	useEffect(() => {
		formik.setFieldValue('nomineeId', selectedEmployee || '');
	}, [selectedEmployee]);

	useEffect(() => {
		if (customFormDefinition) {
			const title = customFormDefinition?.metadata?.title || '';
			formik.setFieldValue(
				'nominationReason',
				`This nomination was submitted using the custom form "${title}".`
			);
		}
	}, [customFormDefinition]);

	const { values, handleSubmit } = formik;

	const nomineeName = useMemo(() => {
		if (!selectedEmployee || !searchedEmployeeData.length) return '';
		const employee = searchedEmployeeData.find(
			(emp) => emp.id === selectedEmployee
		);
		return employee
			? `${employee.firstName || ''} ${employee.lastName || ''}`.trim()
			: '';
	}, [selectedEmployee, searchedEmployeeData]);

	const nominateEmployee = async (formValues, customFormAnswers) => {
		try {
			const payload = {
				nominationConfigurationId: formValues.nominationConfigurationId,
				nomineeId: formValues.nomineeId,
				nominatorId: currentUser.id,
				companyId: currentUser.companyId,
				nominationReason: formValues.nominationReason,
				isWinner: false,
			};

			if (customFormAnswers) {
				payload.customFormAnswers = JSON.stringify(customFormAnswers);
			}

			const { data, error } = await createNomination({
				variables: {
					input: payload,
				},
			});

			if (error) throw error;

			message.success('Employee Nominated Successfully');
			customFormInstance.resetFields();
			closeModal && closeModal();
			return data?.createNomination;
		} catch (e) {
			console.error(e);
			message.error(e?.message || 'Nomination failed! Try again later');
		}
	};

	const confirm = useCallback((customFormAnswers) => {
		Modal.confirm({
			title: 'Confirm',
			content: (
				<>{`Are you sure, you want to nominate ${nomineeName} for ${badgeDetails?.name}?`}</>
			),
			okText: 'Proceed',
			cancelText: 'Cancel',
			onOk: () => {
				nominateEmployee(values, customFormAnswers);
			},
		});
	}, [badgeDetails, nomineeName, values, currentUser]);

	return (
		<Modal
			open={showModal}
			title={'Select Employee To Nominate'}
			closable={true}
			onCancel={closeModal}
			footer={null}
			width={'650px'}
			className="manage-nominated-reward-modal reward-modal"
		>
			<NominateEmployeePresentation
				formik={formik}
				selectedEmployee={selectedEmployee}
				setSelectedEmployee={setSelectedEmployee}
				searchedEmployeeData={searchedEmployeeData}
				setSearchedEmployeeData={setSearchedEmployeeData}
				badgeDetails={badgeDetails}
				endDate={endDate}
				nominationLoading={nominationLoading}
				hasCustomForm={Boolean(customFormId)}
				customFormDefinition={customFormDefinition}
				customFormLoading={customFormLoading}
				customFormInstance={customFormInstance}
			/>
		</Modal>
	);
};

export default withApollo(NominateEmployeeModalContainer);
